<template>
  <!-- prettier-ignore -->
  <div
    class="box"
    :data-product-id="product.id"
    :data-group-id="groupId"
    :data-category-id="categoryId">

    <div class="main product">
      <ovp-typography
        class="product__punchline"
        type="heading04"
        v-text="$t(punchline)"
      />

      <div class="product__costs">
        <ovp-price
          class="product__price"
          :price="pricing.price"
          :prefix="pricePrefix"
          :suffix="$t('product.currency')"
        />

        <div
          v-if="reduction.price > 0"
          class="product__reduction">
          <div class="reduction-instead">
            {{ $t('product.insteadOf')}}
            <ovp-price :price="reduction.price" />
          </div>

          <div class="reduction__info">
            <template v-for="(r, index) in reduction.labels">
              {{ $t(`info.${r.type}`) }} {{ Math.abs(r.amount) }} {{ $t('product.currency') }}<template v-if="reduction.labels.length - 1 !== index">;</template>
            </template>
          </div>
        </div>
      </div>

      <ovp-typography
        class="product__name"
        type="heading03"
        v-text="$t(content).name"
      />

      <ovp-typography
        class="product__description"
        type="body04"
        v-html="$t(content).description"
      />

      <div class="product__content">
        <ul class="product__bullets">
          <li
            v-for="(bullet, key) in $t(content).bulletPoints"
            :key="key"
            v-text="bullet"
          />
        </ul>

        <ovp-message
          v-if="$te(`content.products.${product.id}.promoInfo`)"
          severity="success"
          class="product__message"
          is-html
          :text="$t(`content.products.${product.id}.promoInfo`)"
        />

        <ovp-message
          v-if="additionalInfo"
          class="product__message"
          is-html
          :text="additionalInfo"
        />
      </div>

      <div
        v-if="hasOptions && !readonly"
        class="product__criterias">
        <div
          v-if="hasAccident"
          class="options-accident options">
          <div class="options__title">
            <ovp-typography type="body02">
              <span v-text="optionTitles.accident.title" />
              &nbsp;
              <ovp-icon
                v-tooltip="optionTitles.accident.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </ovp-typography>
          </div>
          <ovp-product-accident
            :option="productPricing"
            @select="selectOption"
          />
        </div>

        <div
          v-if="hasFranchise"
          class="options-franchise options">
          <div class="options__title">
            <ovp-typography type="body02">
              <span v-text="optionTitles.franchise.title" />
              &nbsp;
              <ovp-icon
                v-if="optionTitles.franchise.tooltip"
                v-tooltip="optionTitles.franchise.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </ovp-typography>
          </div>
          <ovp-product-franchise
            inline
            :options="product.prices"
            :source="source"
            @select="selectOption"
          />
        </div>

        <div
          v-if="hasDuration && !isUpgrade"
          class="options-duration options">
          <div class="options__title">
            <ovp-typography type="body02">
              <span v-text="optionTitles.duration.title" />
              &nbsp;
              <ovp-icon
                v-if="optionTitles.duration.tooltip"
                v-tooltip="optionTitles.duration.tooltip"
                art="light"
                html-tooltip
                name="circle-info"
              />
            </ovp-typography>
          </div>
          <ovp-product-duration
            :category-id="categoryId"
            :options="product.prices"
            :product-id="product.id"
            :source="source"
            @select="selectOption"
          />
        </div>
      </div>

      <div
        v-if="product.doctor"
        class="product__doctor">
        <ovp-typography
          type="body02"
          v-text="$t('product.doctor.titleShort')"
        />

        <ovp-product-doctor
          :person-id="personId"
          :product="product"
          :source="source"
          @search="searchMedicalOffice(product)"
        />
      </div>

      <div class="product__actions">
        <div class="product__links">
          <ovp-comparison-link
            v-if="$t(content).benefitPath"
            type="benefit"
            :content="$t(content)"
            :source="SOURCE_PRODUCT_BENEFITS"
            :product="product">
            {{ $t('comparison.link') }}
          </ovp-comparison-link>

          <a
            v-else-if="$t(content).linkToContentPage"
            :href="$t(content).linkToContentPage">
            {{ $t('comparison.productPage') }}
          </a>

          <ovp-comparison-link
            v-if="$t(content).comparisonPath"
            type="comparison"
            :content="$t(content)"
            :source="SOURCE_PRODUCT_COMPARISON"
            :product="product">
            {{ $t(content).comparisonLinkName }}
          </ovp-comparison-link>
        </div>

        <ovp-product-action
          v-if="!readonly"
          :person-id="personId"
          :product="product"
          :selected="product.selected"
          @add="addProduct(product.id, (isUpgrade) ? 'ADD' : 'REPLACE')"
          @remove="removeProduct(product.id)"
          @search="searchMedicalOffice(product)"
        />
      </div>
    </div>

    <div
      v-if="upgrade && !hideUpgrade"
      class="upgrade"
      :data-product-id="upgrade.id">
      <ovp-typography
        class="product__punchline"
        type="heading04"
        v-text="$t('product.upgrade')"
      />

      <div class="product__costs">
        <ovp-price
          class="product__price"
          :price="pricingUpgrade.price"
          :suffix="$t('product.currency')"
        />

        <div
          v-if="reductionUpgrade.price > 0"
          class="product__reduction">
          <div class="reduction-instead">
            {{ $t('product.insteadOf')}}
            <ovp-price :price="reductionUpgrade.price" />
          </div>

          <div class="reduction__info">
            <template v-for="(r, index) in reductionUpgrade.labels">
              {{ $t(`info.${r.type}`) }} {{ Math.abs(r.amount) }} {{ $t('product.currency') }}<template v-if="reductionUpgrade.labels.length - 1 !== index">;</template>
            </template>
          </div>
        </div>
      </div>

      <ovp-typography
        class="product__name"
        type="heading03"
        v-text="$t(`content.products.${upgrade.id}`).name"
      />

      <ovp-typography
        class="product__description"
        type="body04"
        v-text="$t(`content.products.${upgrade.id}`).description"
      />

      <div class="product__actions">
        <div class="product__links" />

        <ovp-product-action
          v-if="!readonly"
          :person-id="personId"
          :product="upgrade"
          :selected="upgrade.selected"
          @add="addProduct(upgrade.id, 'ADD')"
          @remove="removeProduct(upgrade.id)"
        />
      </div>
    </div>

  </div>
</template>

<script>
import OvpMessage from '@/components/Container/Message'
import { mapActions, mapGetters, mapState } from 'vuex'

import OvpComparisonLink from '@/components/Comparison/Link'
import OvpProductAction from '@/components/Product/Action'
import OvpProductDoctor from '@/components/Product/Doctor'
import OvpProductFranchise from '@/components/Product/Franchise'
import OvpProductDuration from '@/components/Product/Duration'

import {
  EVENT_MEDICAL_OFFICE_MODAL_OPEN,
  EVENT_PRODUCT_REMOVED,
  SOURCE_CONFIGURATOR,
  SOURCE_PRODUCT_BENEFITS,
  SOURCE_PRODUCT_COMPARISON,
} from '@/config/events'

import { BasketHelper, PersonHelper, ProductHelper } from '@/helpers'

import { BasketMixin, ProductMixin } from '@/mixins'

export default {
  name: 'ProductBox',

  mixins: [BasketMixin, ProductMixin],

  components: {
    OvpComparisonLink,
    OvpProductAction,
    OvpProductDoctor,
    OvpProductDuration,
    OvpProductFranchise,
    OvpMessage,
  },

  props: {
    categoryId: {
      type: String,
      default: '',
    },

    closable: {
      type: Boolean,
      default: false,
    },

    groupId: {
      type: String,
      required: true,
    },

    hideUpgrade: {
      type: Boolean,
      default: false,
    },

    isUpgrade: {
      type: Boolean,
      default: false,
    },

    options: {
      type: Boolean,
      default: true,
    },

    personId: {
      type: String,
      required: true,
    },

    product: {
      type: Object,
      required: true,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    source: {
      type: String,
      default: SOURCE_CONFIGURATOR,
    },
  },

  data() {
    return {
      SOURCE_PRODUCT_BENEFITS,
      SOURCE_PRODUCT_COMPARISON,
    }
  },

  beforeDestroy() {
    this.$emit('closed')
  },

  computed: {
    ...mapGetters('basket', ['getProduct', 'getProductPrice']),
    ...mapState('basket', ['contractStartDate']),

    additionalInfo() {
      const key = `content.products.${this.product.id}`
      if (PersonHelper.isUnborn(this.personId) && this.$te(`${key}.additionalInfoUnborn`)) {
        return this.$t(`${key}.additionalInfoUnborn`)
      } else if (this.$te(`${key}.additionalInfo`)) {
        return this.$t(`${key}.additionalInfo`)
      }

      return false
    },

    classes() {
      return {
        'box-main': true,
        'box--selected': this.product.selected,
        'box--disabled': !this.product.valid && !this.product.selected,
      }
    },

    hasAccident() {
      return ProductHelper.hasAccident(this.product) && this.optionTitles.accident
    },

    hasDuration() {
      return ProductHelper.hasDuration(this.product) && this.optionTitles.duration
    },

    hasFranchise() {
      return ProductHelper.hasFranchise(this.product) && this.optionTitles.franchise
    },

    hasOptions() {
      return ProductHelper.hasOptions(this.product) && this.options
    },

    optionTitles() {
      return ProductHelper.optionTitles(
        this.$te.bind(this),
        this.$t.bind(this),
        this.product.id,
        this.groupId,
        this.categoryId
      )
    },

    pricing() {
      return this.product.prices.find(p => p.selected) || {}
    },

    pricingUpgrade() {
      return this.upgrade.prices.find(p => p.selected) || {}
    },

    priceClasses() {
      return {
        'box-price': true,
        'box-price--reduced': this.productPricing.reductions.length > 0,
      }
    },

    punchline() {
      const group = `content.groups.${this.groupId}.title`
      const product = `content.products.${this.product.id}.punchline`

      return this.$te(product) ? product : group
    },

    reductionUpgrade() {
      const amount = Math.abs(this.pricingUpgrade.reduction)
      return {
        price: amount > 0 ? this.pricingUpgrade.price + amount : 0,
        labels: this.pricingUpgrade?.reductions ?? [],
      }
    },

    upgrade() {
      const upgrades = this.product?.upgrades ?? []
      return upgrades.map(productId => Object.assign({ id: productId }, this.getProduct(this.personId, productId)))[0]
    },

    upgradeClasses() {
      return {
        upgrade: true,
        'box-upgrade': true,
        'box--selected': this.upgrade.selected,
        'box--disabled': !this.product.selected,
      }
    },

    upgradeContent() {
      return `content.products.${this.upgrade.id}`
    },

    upgradePrice() {
      const data = this.getProductPrice(this.personId, this.upgrade.id)

      if (!data.reductions) data.reductions = []

      return data
    },

    upgradePriceClasses() {
      return {
        'product-price': true,
        'product-price--reduced': this.upgradePrice.reductions.length > 0,
      }
    },
  },

  methods: {
    ...mapActions('basket', ['setBasket']),

    addProduct(productId, mode = 'REPLACE') {
      this.$emit('added', {
        productId,
        personId: this.personId,
        categoryId: this.categoryId,
        mode,
        forceAdd: true,
        hasUpdate: !!this.upgrade,
      })
    },

    removeProduct(productId) {
      return BasketHelper.askChangeConfirmation(this.$t('product.confirmRemoval'))
        .then(() => this.$loading.start())
        .then(() => ProductHelper.removeProduct(this.personId, productId))
        .then(basket => {
          this.$EventBus.$emit(EVENT_PRODUCT_REMOVED, {
            basket,
            categoryId: this.categoryId,
            personId: this.personId,
            productId: this.product.id,
            source: this.source,
          })

          this.$emit('removed', productId)
        })
        .finally(() => this.$loading.end())
    },

    searchMedicalOffice(product) {
      this.$EventBus.$emit(EVENT_MEDICAL_OFFICE_MODAL_OPEN, {
        source: this.source,
        personId: this.personId,
        product,
        callback: () => this.$emit('closed'),
      })
    },
  },
}
</script>

<style name="mobile" scoped>
.box {
  cursor: default;
  display: grid;
  grid-template-rows: 1fr minmax(0, auto);

  .main {
    padding: 12px 0;
  }

  .upgrade {
    padding: 12px 0;
    margin: 12px 0 0 0;
    border-top: 1px solid var(--c-black);
  }

  .product {
    &__punchline,
    &__costs,
    &__name,
    &__description,
    &__content,
    &__criterias,
    &__actions,
    &__doctor {
      padding: 0 12px;
    }

    &__punchline {
    }

    &__costs {
      display: flex;
      flex-direction: column;

      .product__price {
        font-size: 50px;
        line-height: 60px;

        & >>> .price__unavailable {
          font-size: 30px;
        }
      }

      .product__reduction {
        font-size: 14px;
      }
    }

    &__name {
      margin: 16px 0 0 0;
    }

    &__description {
      margin: 8px 0 0 0;
    }

    &__content {
      .product__bullets {
        margin: 8px 0 0 0;
        padding: 0 0 0 20px;
      }

      .product__message {
        margin: 24px 0 0 0;
      }
    }

    &__criterias {
      margin: 16px 0 0 0;

      .options {
        margin: 8px 0 0 0;
      }
    }

    &__doctor {
      margin: 16px 0 0 0;
    }

    &__actions {
      margin: 44px 0 0 0;
      display: flex;
      flex-direction: column;

      .product__links {
        display: flex;
        flex-direction: row;
        gap: 24px;
        line-height: 50px;
      }
    }
  }
}
</style>

<style name="desktop" scoped>
@media (--md) {
  .box {
    .main {
      padding: 24px 0;
    }

    .upgrade {
      padding: 24px 0;
    }

    .product {
      &__punchline,
      &__costs,
      &__name,
      &__description,
      &__content,
      &__criterias,
      &__actions,
      &__doctor {
        padding: 0 24px;
      }

      &__costs {
        flex-direction: row;
        gap: 24px;

        .product__reduction {
          padding: 16px 0 0 0;
        }
      }

      &__actions {
        flex-direction: row;
        justify-content: space-between;

        .product__links {
          gap: 48px;
        }
      }
    }
  }
}
</style>
